import React, {useEffect, useState} from 'react'
import {MainContent, MainLayoutWithoutStretch} from '../../../../Components/Layout/Layout'
import {GoBackHeaderTitle, HeaderRow} from '../../../../Components/Molecules/ViewComponents/ViewHeader'
import {useTranslation} from 'react-i18next'
import {Select} from '../../../../Components/Atoms/Forms'
import {useAppState} from '../../../../state'
import {normalizeLocations} from '../../../../config/utils'
import {AssetData, AssetType, UserLanguage} from '../../../../state/rest'
import {SiteImportExcelUploader} from './SiteImportExcelUploader'
import {Text, Title} from '../../../../sharedComponents/components'
import {InvalidSiteImportRow, parseSiteImportExcel, validateSiteImportRows} from './ExcelUtils'
import {Button, ButtonRowWrap} from '../../../../Components/Atoms/Buttons'
import {useNavigation} from 'react-navi'

const ImportErrorTable = ({invalidRows}: {invalidRows: InvalidSiteImportRow[]}) => {
  const {t} = useTranslation('locations')

  if (invalidRows.length === 0) return null

  return (
    <div style={{marginTop: '2rem'}}>
      <h3>{t('locations:labels.importErrors', 'Rows with errors')}</h3>

      <div
        style={{
          background: '#fff4f4',
          border: '1px solid #f0b4b4',
          borderRadius: '6px',
          padding: '1rem',
          marginBottom: '1rem'
        }}
      >
        {t(
          'locations:messages.importErrorsFound',
          '{{count}} errors found. Fix the Excel file or import only valid rows.',
          {count: invalidRows.length}
        )}
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem'
        }}
      >
        <thead>
          <tr>
            <th style={thStyle}>{t('locations:labels.row', 'Row')}</th>
            <th style={thStyle}>{t('locations:labels.siteName', 'Site name')}</th>
            <th style={thStyle}>{t('locations:labels.field', 'Field')}</th>
            <th style={thStyle}>{t('locations:labels.error', 'Error')}</th>
          </tr>
        </thead>

        <tbody>
          {invalidRows.map((error, index) => (
            <tr key={`${error.rowNumber}-${error.field}-${index}`}>
              <td style={tdStyle}>{error.rowNumber}</td>
              <td style={tdStyle}>{error.siteName || '—'}</td>
              <td style={tdStyle}>{error.field}</td>
              <td style={tdStyle}>{error.error}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.75rem',
  borderBottom: '1px solid #ddd',
  background: '#f7f7f7'
}

const tdStyle: React.CSSProperties = {
  padding: '0.75rem',
  borderBottom: '1px solid #eee'
}

const MultipleSiteForLocation = () => {
  const {t, i18n} = useTranslation('locations')
  const {state, actions} = useAppState()
  const nav = useNavigation()
  const locations = normalizeLocations(state.chainsById[state.selectedChainId!])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [validRows, setValidRows] = useState<any[]>([])
  const [invalidRows, setInvalidRows] = useState<any[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [parsedRows, setParsedRows] = useState<any[]>([])

  useEffect(() => {
    if (parsedRows.length === 0) return
    const result = validateSiteImportRows(parsedRows, UserLanguage.en, t)
    setValidRows(result.validRows)
    setInvalidRows(result.invalidRows)
  }, [i18n.language])

  const handleExcelFileSelected = async (file: File) => {
    const rows = await parseSiteImportExcel(file)
    setParsedRows(rows)
    const result = validateSiteImportRows(rows, UserLanguage.en, t)
    setValidRows(result.validRows)
    setInvalidRows(result.invalidRows)
  }

  const handleFileRemoved = () => {
    setParsedRows([])
    setValidRows([])
    setInvalidRows([])
  }

  const handleImportSites = async () => {
    if (!selectedLocation || validRows.length === 0) return

    setIsImporting(true)

    try {
      const result = await actions.v1.settings.sites.importSites({
        locationId: selectedLocation,
        rows: validRows
      })
      if (state.me?.accessRights.superuser) {
        await actions.getChains()
      }

      await actions.updateMe()
    } catch (error) {
      console.error('Failed to import sites', error)
    } finally {
      setIsImporting(false)
      nav.navigate('/settings/locations/')
    }
  }

  return (
    <MainLayoutWithoutStretch>
      <MainContent variant="white">
        <HeaderRow>
          <GoBackHeaderTitle
            path="/settings/locations"
            label={t('locations:title.addMultipleSiteForLocation', 'Add Multiple Sites for Location')}
            backLabel={t('locations:labels.locations', 'Location settings')}
          />
        </HeaderRow>
        <div style={{margin: '2rem'}}>
          <Select
            required
            returnId={true}
            label={t('locations:labels.location', 'Location')}
            emptyStr={t('locations:labels.chooseLocation', 'Choose location')}
            options={locations.map(location => ({id: location.id, option: location.name}))}
            id="location-select"
            nativeProps={{
              value: selectedLocation,
              onChange: event => setSelectedLocation(event.currentTarget.value)
            }}
          />
          <div style={{margin: '2rem 0'}}>
            <Title style={{marginBottom: '1rem'}} level={6}>
              {t('locations:labels.addFile', 'Add File')}
            </Title>
            <div style={{marginBottom: '1rem'}}>
              <Text size="XS" style={{paddingBottom: '1rem'}}>
                {t(
                  'locations:labels.excelFileInstructions',
                  ' Upload an Excel file with site details. SiteName is required.For actorSuggestion column, names should be seperated by comma. Region defaults to Finland, put english name of the region to be added. Timezone is automatically found from region so you do not need to input'
                )}
              </Text>
            </div>
            <SiteImportExcelUploader
              disabled={!selectedLocation}
              onFileSelected={handleExcelFileSelected}
              onFileRemoved={handleFileRemoved}
            />
          </div>
          {invalidRows.length > 0 && (
            <div>
              <ImportErrorTable invalidRows={invalidRows} />
            </div>
          )}
          {validRows.length > 0 && (
            <div
              style={{
                background: '#f2fbf5',
                border: '1px solid #a8d8b9',
                borderRadius: '6px',
                padding: '1rem',
                marginTop: '2rem'
              }}
            >
              {t('locations:messages.validRowsFound', '{{count}} sites are ready to import.', {
                count: validRows.length
              })}
            </div>
          )}
          {
            <ButtonRowWrap>
              <Button
                variant="secondary"
                disabled={!selectedLocation || validRows.length === 0 || isImporting}
                onClick={handleImportSites}
              >
                {t('locations:actions.importSites', 'Import  sites')}
              </Button>
            </ButtonRowWrap>
          }
        </div>
      </MainContent>
    </MainLayoutWithoutStretch>
  )
}

export default MultipleSiteForLocation
